import getAggregatedData from '../../../helpers/assets/getAggregatedData';
import sdk from '../../../sdk';
import Logger from '../../../services/Logger';
import TYPES from '../../action-types';
import { getPermissions } from '../../helpers/assets';
import getAssetsFromStoreByIds from '../../helpers/assets/getAssetsFromStoreByIds';
import hasAllAssets from '../../helpers/assets/hasAllAssets';
import setAggregatedData from '../../helpers/assets/setAggregatedData';
import getThumbnails from './getThumbnails';

function selectionUnchanged(selectedIds, currentIds) {
  return selectedIds.length === currentIds.length
    && selectedIds.every((id) => currentIds.includes(id));
}

/**
 * Select assets and load the same details payload a manual catalog selection builds.
 * Notification clicks often run before those assets are in the catalog store, so the
 * missing documents are fetched and then aggregated with setAggregatedData.
 * @param {string[]} ids
 * @param {boolean} value
 */
export const setPendingNotificationSelection = (ids, path) => ({
  type: TYPES.ASSETS.SET_PENDING_NOTIFICATION_SELECTION,
  payload: { ids, path },
});

export const clearPendingNotificationSelection = () => ({
  type: TYPES.ASSETS.CLEAR_PENDING_NOTIFICATION_SELECTION,
});

const selectMany = (ids, value, { replace = false } = {}) => async (dispatch, getState) => {
  dispatch({
    type: TYPES.ASSETS.SELECT_MANY,
    payload: { ids, value, ...(replace ? { replace: true } : {}) },
  });
  Logger.log('User', value ? 'ThumbnailSelect' : 'ThumbnailDeselect');

  if (!value || !ids?.length) {
    return;
  }

  const selectedIds = getState().assets.selectedItems;
  const missingIds = selectedIds.filter(
    (id) => !getState().assets.items.some((asset) => asset._id === id),
  );

  if (missingIds.length) {
    try {
      const { data } = await sdk.assets.getMany(missingIds);
      const latestItems = getState().assets.items;
      const fetched = (data?.assets || []).filter(
        (asset) => !latestItems.some((item) => item._id === asset._id),
      );
      if (fetched.length) {
        dispatch({
          type: TYPES.ASSETS.GET_BY_IDS.COMPLETE,
          payload: { items: fetched },
        });
        getThumbnails(fetched.map((asset) => asset._id))(dispatch, getState);
      }
    } catch (error) {
      Logger.error(new Error('Can not load assets for notification selection'), { error }, [
        'GetAssetsByIdsFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
    }
  }

  let latest = getState();
  if (!selectionUnchanged(selectedIds, latest.assets.selectedItems)) {
    return;
  }

  const { user, customFields } = latest;
  let aggregatedData;
  const selectedAssets = getAssetsFromStoreByIds(selectedIds, latest.assets.items).filter(Boolean);

  if (selectedAssets.length === selectedIds.length) {
    aggregatedData = setAggregatedData(
      selectedAssets,
      {},
      user,
      customFields?.items,
    );
  } else {
    aggregatedData = await getAggregatedData(selectedIds, user);
  }

  if (!aggregatedData) {
    return;
  }

  latest = getState();
  if (!selectionUnchanged(selectedIds, latest.assets.selectedItems)) {
    return;
  }

  const rolePermissions = user?.role?.permissions;
  const allowedActions = rolePermissions && hasAllAssets(selectedIds, latest.assets.items)
    ? getPermissions(latest.assets.items, selectedIds, rolePermissions)
    : undefined;

  dispatch({
    type: TYPES.ASSETS.SELECT_MANY,
    payload: {
      ids: selectedIds,
      value: true,
      aggregatedData,
      ...(allowedActions ? { allowedActions } : {}),
    },
  });
  window.dispatchEvent(new Event('selection:switched:on'));
};

export default selectMany;
